<template>
    <div class="layout__main layout__main--module-start">
        <BaseSection class="layout__hero layout__hero">
            <template #title>
                <h1>
                    <template v-if="moduleItem && moduleItem.title">
                        {{ moduleItem.title }}
                    </template>
                    <template v-else>
                        <ContentPlaceholdersText :lines="1" />
                    </template>
                </h1>
            </template>
            <template #description>
                <template v-if="moduleItem">
                    <p v-if="moduleItem.introduction" class="lead">
                        {{ moduleItem.introduction }}
                    </p>
                    <template v-if="moduleItem.description">
                        <!-- eslint-disable vue/no-v-html -->
                        <div v-html="moduleItem.description" />
                        <!-- eslint-enable vue/no-v-html -->
                    </template>
                    <BaseTimeEstimate :time="timing" />
                    <ClientOnly>
                        <LazyBaseModuleProgress>
                            <template #default="{ moduleProgress }">
                                <LazyTheModuleControls
                                    v-if="module && (!moduleProgress(module.slug) || moduleProgress(module.slug) === 0)"
                                    variant="vertical"
                                    :next-item="firstChapter"
                                    next-text="Start the module"
                                    next-action="start-module"
                                    :exit-item="{'uri': originPath}"
                                    exit-text="Exit module"
                                />
                                <LazyTheModuleControls
                                    v-else-if="module"
                                    variant="vertical"
                                    :next-item="firstChapter"
                                    next-text="Continue the module"
                                    next-action="continue-module"
                                    :exit-item="{'uri': originPath}"
                                    exit-text="Exit module"
                                />
                            </template>
                        </LazyBaseModuleProgress>
                    </ClientOnly>
                </template>
                <template v-else>
                    <ContentPlaceholdersText :lines="2" />
                </template>
            </template>
            <BaseFigure
                v-if="moduleItem && moduleItem.image && moduleItem.image.length"
                :src="moduleItem.image[0].large"
                :alt="moduleItem.image[0].alt || ''"
                :srcset="getSrcSet(moduleItem.image[0],'headerImage')"
                sizes="(min-width: 1401px) 305px, (min-width: 993px) 230px, 50vw"
            />
        </BaseSection>
    </div>
</template>
<script setup lang="ts">
import ModuleQuery from '~/graphql/queries/Module.graphql';
import ModuleItemQuery from '~/graphql/queries/ModuleItem.graphql';
import useSrcSets from '~/utils/srcset';
import BaseTimeEstimate from '~/components/BaseTimeEstimate/BaseTimeEstimate.vue';

definePageMeta({
    layout: 'module',
    pageTransition: {
        name: 'none',
    }
});

const t = useTranslation('components');

const { variables } = useCraftGraphqlSlug();
const { activeSite } = useMultisite();
const {getSrcSet} = useSrcSets();

const slug = useRoute().params?.module;

const { data: moduleDataRef } = await useLazyAsyncQuery({
    query: ModuleQuery,
    variables: { ...toValue(variables), slug }
});

const moduleData = computed(() => (toValue(moduleDataRef) as { module?: any })?.module);
const { getTimeEstimate, module } = useModule(moduleData);

const firstChapter = computed(() => toValue(module)?.chapters?.[0]);

const moduleItemResponse = await useLazyAsyncQuery({
    query: ModuleItemQuery,
    variables: { ...toValue(variables), slug, level: 1 }
});

const { craftEntry: moduleItem } = usePageQuery(
    moduleItemResponse as any,
    { entryKey: 'item', activeSite }
);

const timing = computed(() => {
    const timeEstimate = getTimeEstimate(toValue(moduleItem)?.id) || 0;
    return t('module-full-time').replace('{{timing}}', timeEstimate);
});

const { initModule, getOriginPath, moduleProgress: progress } = useProgressStorage();
const { trackEvent } = useCustomGtmEvent();
const originPath = getOriginPath();

watch(() => moduleData.value, () => {
    if (!moduleData.value) {
        return;
    }

    const {
        id,
        title,
        chapters
    } = moduleData.value;

    initModule(slug, id, title, chapters);

    trackEvent({
        event: 'module_view',
        'module_name': title || null,
        'module_progress': progress(slug) || null
        
    });
}, { immediate: true });
</script>

<style lang="less">
.layout__main--module-start {
    background: var(--color-primary-300, #B5E1FF);

    .lead {
        font-size: var(--font-size-lg);
        margin-bottom: 2.5rem;
    }

    @media @q-md-max {
        .section {
            margin-top: 0;
        }
    }

    // Remove custom line breaks on this page
    br {
        display: none;
    }
}

.layout__main--module-start .section.layout__hero {
    .section__wrapper {
        display: flex;
    }

    .figure {
        width: 100%;
        aspect-ratio: 1/1;

        img {
            height: 100%;
        }
    }

    @media @q-md-min {
        .section__header {
            flex: 0 0 50%;
            width: 50%;
        }

        .section__content {
            width: 50%;
        }
    }

    @media @q-md-max {
        .section__wrapper {
            flex-direction: column;
            display: flex;
        }

        .section__header {
            order: 1;
        }
    }
}
</style>
